import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoStar, IoStarHalf } from "react-icons/io5";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";

const CustomerReviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const autoplayRef = useRef(null);

  const reviews = [
    {
      id: 1,
      name: "Sarah Anderson",
      role: "Premium Customer",
      heading: "Exceptional Quality",
      description: "The attention to detail and premium quality exceeded all my expectations. Truly a luxury experience from start to finish.",
      stars: 5,
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
      date: "March 15, 2025"
    },
    {
      id: 2,
      name: "Michael Chen",
      role: "VIP Member",
      heading: "Luxurious Experience",
      description: "Every detail was meticulously crafted. The premium service and attention to detail made me feel truly special.",
      stars: 5,
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
      date: "March 12, 2025"
    },
    {
      id: 3,
      name: "Emma Thompson",
      role: "Elite Client",
      heading: "Outstanding Service",
      description: "A perfect blend of luxury and sophistication. The personalized attention I received was remarkable.",
      stars: 5,
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
      date: "March 10, 2025"
    },
    {
      id: 4,
      name: "James Wilson",
      role: "Verified Purchase",
      heading: "Pure Elegance",
      description: "The epitome of luxury and class. Every interaction was refined and sophisticated. Highly recommended!",
      stars: 5,
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
      date: "March 8, 2025"
    },
    {
      id: 5,
      name: "Isabella Martinez",
      role: "Luxury Enthusiast",
      heading: "Impeccable Design",
      description: "An extraordinary experience that surpassed all expectations. The attention to detail is simply remarkable.",
      stars: 5,
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
      date: "March 5, 2025"
    },
    {
      id: 6,
      name: "Sami",
      role: "Luxury Enthusiast",
      heading: "Impeccable Design",
      description: "An extraordinary experience that surpassed all expectations. The attention to detail is simply remarkable.",
      stars: 5,
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
      date: "March 5, 2025"
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      
      // More granular responsive adjustments
      if (width < 640) {
        setItemsPerPage(1);
      } else if (width < 1024) {
        setItemsPerPage(2);
      } else if (width < 1280) {
        setItemsPerPage(3);
      } else {
        setItemsPerPage(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (autoplay) {
      autoplayRef.current = setInterval(() => {
        handleNext();
      }, 5000);
    }
    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [autoplay, currentIndex]);

  const totalPages = Math.ceil(reviews.length / itemsPerPage);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const pauseAutoplay = () => {
    setAutoplay(false);
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  };

  const resumeAutoplay = () => {
    setAutoplay(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section with enhanced typography */}
        <div className="text-center mb-16 md:mb-24">
          <span className="text-sm uppercase tracking-widest text-gray-500 mb-2 block">What Our Clients Say</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light tracking-wide text-gray-900 mb-4">
            Client <span className="font-medium">Testimonials</span>
          </h2>
          <div className="w-24 h-0.5 bg-black mx-auto mb-8 relative">
            <div className="absolute w-6 h-0.5 bg-black -right-8"></div>
            <div className="absolute w-6 h-0.5 bg-black -left-8"></div>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base md:text-lg">
            Discover what our esteemed clients have to say about their luxury experience with our premium fragrances
          </p>
        </div>

        {/* Reviews Carousel with improved responsive design */}
        <div className="relative"
             onMouseEnter={pauseAutoplay}
             onMouseLeave={resumeAutoplay}
             onTouchStart={pauseAutoplay}
             onTouchEnd={resumeAutoplay}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
            >
              {reviews
                .slice(
                  currentIndex * itemsPerPage,
                  (currentIndex * itemsPerPage) + itemsPerPage
                )
                .map((review) => (
                  <motion.div
                    key={review.id}
                    className="bg-white rounded-xl p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_15px_60px_rgba(0,0,0,0.1)] transition-all duration-500 border border-gray-50 backdrop-blur-sm"
                    whileHover={{ y: -8, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  >
                    {/* Premium quotation mark */}
                    <div className="absolute -top-4 -left-2 text-6xl text-gray-100 font-serif opacity-50">"</div>
                    
                    {/* Review Header with improved layout */}
                    <div className="flex items-center gap-4 mb-6 relative z-10">
                      <div className="relative">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover shadow-md"
                        />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-black rounded-full flex items-center justify-center">
                          <IoStar className="text-yellow-400 w-4 h-4" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 text-lg">{review.name}</h3>
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-1 h-1 rounded-full bg-gray-300"></span>
                          <p className="text-sm text-gray-500">{review.role}</p>
                        </div>
                      </div>
                    </div>

                    {/* Review Content with improved typography */}
                    <div>
                      <h4 className="text-xl md:text-2xl font-medium text-gray-900 mb-3">
                        {review.heading}
                      </h4>
                      <p className="text-gray-600 mb-4 leading-relaxed text-sm md:text-base">
                        {review.description}
                      </p>
                    </div>

                    {/* Review Footer with enhanced design */}
                    <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-100">
                      <div className="flex gap-1">
                        {[...Array(review.stars)].map((_, i) => (
                          <IoStar key={i} className="text-yellow-400 w-5 h-5" />
                        ))}
                      </div>
                      <span className="text-xs md:text-sm text-gray-500 italic">{review.date}</span>
                    </div>
                  </motion.div>
                ))}
            </motion.div>
          </AnimatePresence>

          {/* Enhanced Navigation Controls */}
          <div className="flex flex-row justify-center items-center gap-4 mt-12">
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-black hover:text-white transition-colors border border-gray-100 group"
              disabled={currentIndex === 0}
              aria-label="Previous testimonials"
            >
              <IoChevronBack size={20} className="group-hover:scale-110 transition-transform" />
            </button>

            <div className="flex gap-2 mx-2">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentIndex === index 
                    ? "w-10 bg-black" 
                    : "w-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Go to testimonial page ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-black hover:text-white transition-colors border border-gray-100 group"
              disabled={currentIndex === totalPages - 1}
              aria-label="Next testimonials"
            >
              <IoChevronForward size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReviews;